/**
 * readinessService.js — Backend interfaces for the Readiness experience.
 *
 * Every network concern is behind one of these functions so swapping the
 * stubs for real endpoints later is a one-file change:
 *
 *   askOwenSideQuestion — LLM answer for the "ask Owen anything" lane
 *                         (Supabase edge function `owen-apply`, mode
 *                         'side-question'; canned keyword fallback).
 *   createPasswordlessAccount / sendSignInCode / verifySignInCode
 *                       — passwordless account stubs.
 *   sendProgressReport  — email-my-report stub.
 *   persistApplication  — application persistence stub.
 */
import { supabase, supabaseEphemeralAuth, isSupabaseConfigured } from '../lib/supabase';
import {
  getCannedAnswer,
  OWEN_SIDE_ANSWER_SYSTEM_PROMPT,
} from '../config/readinessQuestions';

const SIDE_ANSWER_TIMEOUT_MS = 9000;

/**
 * Answer a borrower side question with the partial profile as context.
 * Falls back to the canned keyword answers after 9s or on any failure.
 * Only non-PII assessment answers are ever sent (see the edge function's
 * sanitizer as well); the system prompt reiterates Owen's AI/no-PII stance.
 *
 * @param {string} question
 * @param {Object} profile — partial assessment answers (no PII collected here)
 * @returns {Promise<{ text: string, source: 'llm' | 'canned' }>}
 */
export async function askOwenSideQuestion(question, profile = {}) {
  const fallback = { text: getCannedAnswer(question), source: 'canned' };
  if (!isSupabaseConfigured) return fallback;

  const timeout = new Promise((resolve) =>
    setTimeout(() => resolve(null), SIDE_ANSWER_TIMEOUT_MS),
  );
  const call = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke('owen-apply', {
        body: {
          mode: 'side-question',
          question,
          profile,
          systemPrompt: OWEN_SIDE_ANSWER_SYSTEM_PROMPT,
        },
      });
      if (error) throw error;
      const text = String(data?.answer || '').trim();
      return text.length >= 10 ? text : null;
    } catch (e) {
      console.warn('[readiness] side-question fallback:', e?.message || e);
      return null;
    }
  })();

  const text = await Promise.race([call, timeout]);
  return text ? { text, source: 'llm' } : fallback;
}

/**
 * Create a passwordless account keyed by email. The account is actually
 * materialized in Supabase Auth on the first OTP sign-in (shouldCreateUser);
 * the email gate only records the address and lets the borrower continue.
 */
export async function createPasswordlessAccount(email) {
  return { ok: true, email };
}

/**
 * Send a one-time 8-digit sign-in code via Supabase Auth email OTP
 * (code length is set by the Supabase project's auth config).
 * Creates the account on first sign-in. Falls back to stub-ok when
 * Supabase isn't configured (offline dev).
 *
 * Uses the session-less auth client: given the sensitivity of what borrowers
 * enter here, nothing is persisted to the browser — leaving the session or
 * closing the window means signing in again with a fresh code.
 *
 * NOTE: the Supabase "Magic Link" email template must include {{ .Token }}
 * so the email carries the 8-digit code (Dashboard → Auth → Email Templates).
 */
export async function sendSignInCode(email) {
  if (!isSupabaseConfigured) return { ok: true, email, stub: true };
  const { error } = await supabaseEphemeralAuth.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: true },
  });
  if (error) {
    console.warn('[readiness] sendSignInCode failed:', error.message);
    return { ok: false, email, error: friendlyOtpError(error) };
  }
  return { ok: true, email };
}

/**
 * Verify the emailed code against Supabase Auth. On success the borrower
 * is authenticated for this visit only — the session lives in memory and
 * is gone when they leave or close the window. Falls back to accepting any
 * 6 digits when Supabase isn't configured (offline dev, prototype behavior).
 */
export async function verifySignInCode(email, code) {
  if (!/^\d{6}$/.test(code)) return { ok: false, email };
  if (!isSupabaseConfigured) return { ok: true, email, stub: true };
  const { data, error } = await supabaseEphemeralAuth.auth.verifyOtp({
    email,
    token: code,
    type: 'email',
  });
  if (error || !data?.session) {
    console.warn('[readiness] verifySignInCode failed:', error?.message);
    return { ok: false, email, error: friendlyOtpError(error) };
  }
  return { ok: true, email };
}

/** End the in-memory session on sign-out (fire-and-forget safe). */
export async function signOutAccount() {
  if (!isSupabaseConfigured) return { ok: true };
  const { error } = await supabaseEphemeralAuth.auth.signOut();
  if (error) console.warn('[readiness] signOut failed:', error.message);
  return { ok: !error };
}

function friendlyOtpError(error) {
  const msg = String(error?.message || '');
  const code = String(error?.code || '');
  // 60s cooldown between OTP emails: "…you can only request this after N seconds."
  const wait = msg.match(/after (\d+) second/);
  if (wait) return 'A code was just sent \u2014 you can request another in ' + wait[1] + ' seconds.';
  if (code === 'over_email_send_rate_limit' || /rate limit|too many/i.test(msg)) {
    return 'Too many codes requested \u2014 give it a few minutes, then try again.';
  }
  if (/expired|invalid/i.test(msg)) return 'That code didn\u2019t match or has expired \u2014 try again or resend.';
  return 'Something went wrong on our end \u2014 please try again.';
}

/**
 * Email a progress-report snapshot via the `send-progress-report` edge
 * function (Resend-backed). Fire-and-forget from the modal's perspective —
 * failures are logged, not surfaced, matching the prototype's optimistic UX.
 */
export async function sendProgressReport(email, snapshot) {
  if (!isSupabaseConfigured) return { ok: true, email, snapshot, stub: true };
  try {
    const { data, error } = await supabase.functions.invoke('send-progress-report', {
      body: { email, snapshot },
    });
    if (error) throw error;
    return { ok: true, email, messageId: data?.messageId || null };
  } catch (e) {
    console.warn('[readiness] progress report send failed:', e?.message || e);
    return { ok: false, email };
  }
}

/** Persist saved application sections under the account. Stub. */
export async function persistApplication(email, { answers, sections, selectedProgram }) {
  return { ok: true, email, answers, sections, selectedProgram };
}
